import sourceUtils from '@/features/Feedback/field/Source/source.utils';
import { FILTER_OPERATOR } from '@/types/dataTableConst';
import {
  COLUMN_FILTER_STATUS_KEY_MAP,
  READONLY_COLUMN_KEY,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';

import { queryArrToObj } from './util';

const updateFilterQueryFormats = (
  currFilterSetQuery: FilterSetQuery,
  filterQueryFormat: FilterQueryFormatT,
): FilterSetQuery => {
  const newFilterQueryFormats = [...currFilterSetQuery.filterQueryFormats];

  const targetIndex = newFilterQueryFormats.findIndex(
    (format) => format.columnKey === filterQueryFormat.columnKey,
  );

  if (targetIndex === -1) {
    newFilterQueryFormats.push(filterQueryFormat);
  } else {
    if (filterQueryFormat.filterValues.length === 0) {
      newFilterQueryFormats.splice(targetIndex, 1);
    } else {
      newFilterQueryFormats.splice(targetIndex, 1, filterQueryFormat);
    }
  }

  return {
    ...currFilterSetQuery,
    filterQueryFormats: newFilterQueryFormats,
  };
};

const getSoruceFilters = (
  sourceKey: string,
): {
  values: string[];
  operator: FILTER_OPERATOR;
} => {
  const OPERATORS: FILTER_OPERATOR[] = [
    FILTER_OPERATOR.OR,
    FILTER_OPERATOR.AND,
  ];

  for (const operator of OPERATORS) {
    const splitted = sourceKey.split(operator);
    if (splitted.length > 1) {
      return {
        values: splitted,
        operator,
      };
    }
  }

  return {
    values: [sourceKey],
    operator: FILTER_OPERATOR.OR,
  };
};

/**
 * @description
 * source key를 filter query format으로 변환
 * @input
 * 'source1%%domain1%%channel1'
 * @output
 * [{ key: 'source', value: 'source1' },
 * { key: 'source_domain', value: 'domain1' },
 * { key: 'source_ch', value: 'channel' }]
 */
const convertSourceKeyToFilterQueryArray = (sourceKey: string): QueryT[] => {
  const { values: filters, operator } = getSoruceFilters(sourceKey);
  const arr: QueryT[] = [];

  const sources: Set<string> = new Set();
  const domains: Set<string> = new Set();
  const channels: Set<string> = new Set();

  for (const filter of filters) {
    const [source, domain, channel] = sourceUtils.parseSourceKey(filter);
    if (source) sources.add(source);
    if (domain) domains.add(domain);
    if (channel) channels.add(channel);
  }

  if (sources.size > 0) {
    arr.push({
      key: SOURCE_COLUMNS.SOURCE_TYPE,
      value: [...sources].join(operator),
    });
  }

  if (domains.size > 0) {
    arr.push({
      key: 'source_domain',
      value: [...domains].join(operator),
    });
  }

  if (channels.size > 0) {
    arr.push({
      key: 'source_ch',
      value: [...channels].join(operator),
    });
  }

  return arr;
};

const findSelectedChild = (
  currFilterQueryFormat: FilterQueryFormatT,
  option: FilterOptionItemT,
): boolean => {
  return !!currFilterQueryFormat.filterValues.find((filter) => {
    if (filter.filterKey === option.filterKey) return false;
    const findFromChild = (op: FilterOptionItemT): boolean => {
      for (const child of op.filterList || []) {
        if (child.filterKey === filter.filterKey) return true;
        return findFromChild(child);
      }
      return false;
    };
    return findFromChild(option);
  });
};

const getFilterStatusQuery = (
  filterSetQuery: FilterSetQuery,
  disableReadOnly?: boolean,
): QueryT[] => {
  const result: QueryT[] = [];
  filterSetQuery.filterQueryFormats.forEach((filterQueryFormat) => {
    const columnKey =
      COLUMN_FILTER_STATUS_KEY_MAP[filterQueryFormat.columnKey] ||
      filterQueryFormat.columnKey;
    if (columnKey === SOURCE_COLUMNS.SOURCE_TYPE) {
      // SOURCE의 경우 source, source_domain, source_ch 로 query분할
      const sourceQueryArr = convertSourceKeyToFilterQueryArray(
        filterQueryFormat.filterValues
          .map((value) => value.filterKey)
          .join(filterQueryFormat.operator),
      );
      result.push(...sourceQueryArr);
    } else {
      const value = filterQueryFormat.filterValues
        .map((value) => value.filterKey)
        .join(filterQueryFormat.operator);
      result.push({
        key: columnKey,
        value,
      });
    }
    const readonlyColumnKey = READONLY_COLUMN_KEY[columnKey];
    if (!disableReadOnly && readonlyColumnKey) {
      const filterName = filterQueryFormat.filterValues
        .map((value) => value.filterName)
        .join(filterQueryFormat.operator);
      const targetIndex = result.findIndex(
        (query) => query.key === readonlyColumnKey,
      );
      if (targetIndex === -1) {
        result.push({
          key: readonlyColumnKey,
          value: filterName,
        });
      } else {
        result.splice(targetIndex, 1, {
          key: readonlyColumnKey,
          value: filterName,
        });
      }
    }
  });
  return result;
};

const getOperator = (string: string) => {
  if (string.includes(FILTER_OPERATOR.AND)) return FILTER_OPERATOR.AND;
  return FILTER_OPERATOR.OR;
};

const getFilterStatusLabelQuery = (filterSetQuery: FilterSetQuery) => {
  const result: {
    key: string;
    names: string[];
  }[] = [];
  filterSetQuery.filterQueryFormats.forEach((filterQueryFormat) => {
    const columnKey =
      COLUMN_FILTER_STATUS_KEY_MAP[filterQueryFormat.columnKey] ||
      filterQueryFormat.columnKey;
    const names = filterQueryFormat.filterValues.reduce((acc, value) => {
      if (value.filterName) {
        acc.push(value.filterName);
      } else {
        acc.push(value.filterKey.toString());
      }
      return acc;
    }, [] as string[]);
    result.push({
      key: columnKey,
      names,
    });
  });

  return result;
};

const getFilterStatusQueryObj = (filterSetQuery: FilterSetQuery): QueryObj => {
  const queryArr = getFilterStatusQuery(filterSetQuery);
  return queryArrToObj(queryArr);
};

const convertFilterSetQueryToKeyLabelSetFilter = (
  filterSetQuery: FilterSetQuery,
): KeyLabelSetFilter => {
  const queryArr = filterUtils.getFilterStatusQuery(filterSetQuery, true);
  const labelArr = filterUtils.getFilterStatusLabelQuery(filterSetQuery);
  const keyLabelSetFilter = {
    set: queryArr.reduce(
      (acc, query) => {
        if (query.value === null) return acc;
        acc[query.key] = query.value.toString();
        return acc;
      },
      {} as Record<string, string>,
    ),
    label: labelArr.reduce(
      (acc, query) => {
        acc[query.key] = query.names;
        return acc;
      },
      {} as Record<string, string[]>,
    ),
  };
  return keyLabelSetFilter;
};

const addFilterValue = (
  currFilterSetQuery: FilterSetQuery,
  filterQueryFormat: FilterQueryFormatT,
): FilterQueryFormatT => {
  const newFilterQueryFormats = [...currFilterSetQuery.filterQueryFormats];
  const targetIndex = newFilterQueryFormats.findIndex(
    (format) => format.columnKey === filterQueryFormat.columnKey,
  );

  if (targetIndex === -1) {
    return filterQueryFormat;
  } else {
    const targetFilterFormat = newFilterQueryFormats[targetIndex];
    const newFilterValues = [...targetFilterFormat.filterValues];

    for (const filterValue of filterQueryFormat.filterValues) {
      if (
        newFilterValues.find(
          (value) => value.filterKey === filterValue.filterKey,
        )
      ) {
        continue;
      }
      newFilterValues.push(filterValue);
    }

    const newFilterQueryFormat = {
      ...newFilterQueryFormats[targetIndex],
      filterValues: newFilterValues,
    };

    return newFilterQueryFormat;
  }
};

const flatFilterList = (list: FilterOptionItemT[]): FilterOptionItemT[] => {
  return list.flatMap((item): FilterOptionItemT[] => {
    return [item, ...flatFilterList(item.filterList || [])];
  });
};

const filterUtils = {
  findSelectedChild,
  getSoruceFilters,
  updateFilterQueryFormats,
  convertSourceKeyToFilterQueryArray,
  getFilterStatusQuery,
  getFilterStatusQueryObj,
  getFilterStatusLabelQuery,
  getOperator,
  convertFilterSetQueryToKeyLabelSetFilter,
  addFilterValue,
  flatFilterList,
};

export default filterUtils;
